import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GraduationCap, Loader2, MailCheck, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;

const errText = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(' ');
  return 'Something went wrong. Please try again.';
};

const Shell = ({ title, subtitle, children, testId }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] px-4 py-12" data-testid={testId}>
    <div className="w-full max-w-md">
      <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
        <div className="w-11 h-11 bg-[#20acf6] rounded-xl flex items-center justify-center">
          <GraduationCap className="w-6 h-6 text-white" />
        </div>
        <span className="font-semibold text-xl text-[#1e293b]">Common Impact</span>
      </Link>
      <Card className="shadow-lg bg-white">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-[#1e293b]">{title}</CardTitle>
          <CardDescription className="text-[#1e293b]/60">{subtitle}</CardDescription>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="text-[#20acf6] hover:text-[#3bb8f7] font-medium" data-testid="back-to-login-link">Back to sign in</Link>
      </p>
    </div>
  </div>
);

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try { await axios.post(`${API}/forgot-password`, { email }); setSent(true); }
    catch (err) { setError(errText(err)); }
    finally { setLoading(false); }
  };
  return (
    <Shell title="Forgot your password?" subtitle="We'll email you a secure link to set a new one" testId="forgot-password-page">
      {sent ? (
        <div className="text-center space-y-3 py-4" data-testid="forgot-password-sent">
          <MailCheck className="h-10 w-10 text-[#20acf6] mx-auto" />
          <p className="text-sm text-slate-600">If an account exists for <strong>{email}</strong>, a reset link is on its way. It expires in 1 hour.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert><AlertDescription data-testid="forgot-password-error">{error}</AlertDescription></Alert>}
          <div className="space-y-2">
            <Label htmlFor="fp-email">Email</Label>
            <Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="forgot-password-email" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="forgot-password-submit">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send reset link
          </Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return setError('Password must be at least 8 characters.');
    if (pw !== pw2) return setError('Passwords do not match.');
    setLoading(true); setError('');
    try { await axios.post(`${API}/reset-password`, { token, new_password: pw }); setDone(true); }
    catch (err) { setError(errText(err)); }
    finally { setLoading(false); }
  };
  return (
    <Shell title="Set a new password" subtitle="Choose a password of at least 8 characters" testId="reset-password-page">
      {done ? (
        <div className="text-center space-y-4 py-4" data-testid="reset-password-done">
          <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
          <p className="text-sm text-slate-600">Your password has been updated.</p>
          <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7]"><Link to="/login" data-testid="reset-go-login">Sign in</Link></Button>
        </div>
      ) : !token ? (
        <p className="text-sm text-slate-600 text-center" data-testid="reset-password-missing-token">This link is incomplete. <Link to="/forgot-password" className="text-[#20acf6]">Request a new one</Link>.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert><AlertDescription data-testid="reset-password-error">{error}</AlertDescription></Alert>}
          <div className="space-y-2">
            <Label htmlFor="rp-1">New password</Label>
            <Input id="rp-1" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="reset-password-new" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rp-2">Confirm new password</Label>
            <Input id="rp-2" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" data-testid="reset-password-confirm" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="reset-password-submit">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
          </Button>
        </form>
      )}
    </Shell>
  );
}
